// src/hooks/useKeyboardShortcuts.js
import React, { useEffect, useCallback, useRef } from 'react';
import { useIsMobile } from './useMediaQuery'; // 👈 NUEVO IMPORT

/**
 * 🎯 Hook useKeyboardShortcuts - Gerenciamento profissional de atalhos de teclado
 * 
 * Funcionalidades:
 * - ⌨️ Atalhos globais com prefixo (Ctrl, Alt, Shift)
 * - 🔄 Prevenção inteligente de conflitos
 * - 📝 Suporte a modais e inputs
 * - 🎯 Feedback visual opcional
 * - ⚡ Debounce para evitar execuções múltiplas
 * - ♿ Acessibilidade completa
 * - 📱 Desativado automaticamente em dispositivos móveis
 */

const DEFAULT_OPTIONS = {
  preventDefault: true,
  stopPropagation: false,
  ignoreInputs: true, // Não dispara quando em inputs
  ignoreModals: false, // Dispara mesmo com modal aberto
  debounceTime: 150, // ms entre execuções
  showToast: false, // Mostrar feedback visual
  requireExact: true, // Requer combinação exata de teclas
  disabled: false // Desabilitar atalhos (para mobile)
};

const isInputElement = (element) => {
  if (!element) return false;
  const tagName = element?.tagName?.toLowerCase();
  return tagName === 'input' || 
         tagName === 'textarea' || 
         tagName === 'select' ||
         element?.isContentEditable ||
         element?.getAttribute('role') === 'textbox';
};

const getKeyComboString = (event) => {
  const parts = [];
  if (event.ctrlKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  if (event.metaKey) parts.push('Cmd');
  
  // Tecla principal (ignora modificadores)
  const key = event.key.length === 1 ? event.key.toUpperCase() : event.key;
  if (key !== 'Control' && key !== 'Alt' && key !== 'Shift' && key !== 'Meta') {
    parts.push(key);
  }
  
  return parts.join('+');
};

const normalizeCombo = (combo) => {
  if (!combo) return '';
  return combo
    .split('+')
    .map(part => {
      const normalized = part.trim();
      // Normaliza nomes de teclas
      const lowerPart = normalized.toLowerCase();
      if (lowerPart === 'ctrl' || lowerPart === 'control') return 'Ctrl';
      if (lowerPart === 'cmd' || lowerPart === 'command' || lowerPart === 'windows') return 'Cmd';
      if (lowerPart === 'option' || lowerPart === 'opt') return 'Alt';
      if (lowerPart === 'shift') return 'Shift';
      if (lowerPart === 'escape' || lowerPart === 'esc') return 'Escape';
      if (lowerPart === 'enter') return 'Enter';
      if (lowerPart === 'delete' || lowerPart === 'del') return 'Delete';
      if (lowerPart === 'insert') return 'Insert';
      if (lowerPart === 'home') return 'Home';
      if (lowerPart === 'end') return 'End';
      if (lowerPart === 'pageup') return 'PageUp';
      if (lowerPart === 'pagedown') return 'PageDown';
      if (lowerPart === 'arrowup') return 'ArrowUp';
      if (lowerPart === 'arrowdown') return 'ArrowDown';
      if (lowerPart === 'arrowleft') return 'ArrowLeft';
      if (lowerPart === 'arrowright') return 'ArrowRight';
      return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    })
    .sort((a, b) => {
      // Ordem: Modificadores primeiro, depois tecla principal
      const modifiers = ['Ctrl', 'Alt', 'Shift', 'Cmd'];
      const aIsMod = modifiers.includes(a);
      const bIsMod = modifiers.includes(b);
      if (aIsMod && !bIsMod) return -1;
      if (!aIsMod && bIsMod) return 1;
      return 0;
    })
    .join('+');
};

const combosMatch = (combo1, combo2, exact = true) => {
  if (!combo1 || !combo2) return false;
  
  const normalized1 = normalizeCombo(combo1);
  const normalized2 = normalizeCombo(combo2);
  
  if (exact) {
    return normalized1 === normalized2;
  }
  
  // Match parcial (ex: "Ctrl+S" corresponde a "Ctrl+Shift+S"?)
  const parts1 = normalized1.split('+');
  const parts2 = normalized2.split('+');
  
  return parts1.every(part => parts2.includes(part));
};

export const useKeyboardShortcuts = (shortcuts, options = {}) => {
  const isMobile = useIsMobile(); // 👈 DETECTAR MÓVIL
  const config = { ...DEFAULT_OPTIONS, ...options };
  const lastExecuted = useRef({});
  const activeShortcuts = useRef(new Map());

  // Desabilitar atalhos em dispositivos móveis
  const effectiveConfig = {
    ...config,
    disabled: config.disabled || isMobile
  };

  // Registrar atalhos
  const registerShortcut = useCallback((combo, handler, shortcutOptions = {}) => {
    if (!combo) return () => {};
    
    const normalizedCombo = normalizeCombo(combo);
    activeShortcuts.current.set(normalizedCombo, {
      handler,
      options: { ...effectiveConfig, ...shortcutOptions }
    });
    
    return () => {
      activeShortcuts.current.delete(normalizedCombo);
    };
  }, [effectiveConfig]);

  // Handler principal de teclado
  const handleKeyDown = useCallback((event) => {
    // Se estiver desabilitado, não faz nada
    if (effectiveConfig.disabled) return;

    // Ignorar se estiver em input (quando configurado)
    if (effectiveConfig.ignoreInputs && isInputElement(event.target)) {
      return;
    }

    // Verificar se há modal aberto (quando configurado)
    if (!effectiveConfig.ignoreModals) {
      const modalOpen = document.querySelector('[role="dialog"], [class*="modal"]');
      if (modalOpen) return;
    }

    const pressedCombo = getKeyComboString(event);
    
    // Verificar todos os atalhos registrados
    for (const [registeredCombo, shortcut] of activeShortcuts.current) {
      if (combosMatch(pressedCombo, registeredCombo, effectiveConfig.requireExact)) {
        
        // Debounce para evitar execuções múltiplas
        const now = Date.now();
        const lastTime = lastExecuted.current[registeredCombo] || 0;
        const debounceTime = shortcut.options.debounceTime || effectiveConfig.debounceTime;
        
        if (now - lastTime < debounceTime) {
          return;
        }
        
        lastExecuted.current[registeredCombo] = now;
        
        // Prevenir comportamento padrão
        if (shortcut.options.preventDefault) {
          event.preventDefault();
        }
        
        // Parar propagação
        if (shortcut.options.stopPropagation) {
          event.stopPropagation();
        }
        
        // Executar handler
        shortcut.handler(event);
        
        // Feedback visual (opcional)
        if (shortcut.options.showToast) {
          // Você pode integrar com seu sistema de notificações aqui
          console.log(`🔧 Atalho executado: ${registeredCombo}`);
        }
        
        break;
      }
    }
  }, [effectiveConfig]);

  // Registrar todos os atalhos passados como objeto
  useEffect(() => {
    if (!shortcuts) return;
    
    const unregisterFunctions = [];
    
    if (typeof shortcuts === 'object') {
      Object.entries(shortcuts).forEach(([combo, handler]) => {
        if (typeof handler === 'function') {
          const unregister = registerShortcut(combo, handler);
          unregisterFunctions.push(unregister);
        } else if (Array.isArray(handler)) {
          // Formato: [handler, options]
          const [fn, opts] = handler;
          const unregister = registerShortcut(combo, fn, opts);
          unregisterFunctions.push(unregister);
        }
      });
    }
    
    // Adicionar listener global (só se não estiver desabilitado)
    if (!effectiveConfig.disabled) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      unregisterFunctions.forEach(unregister => unregister());
    };
  }, [shortcuts, registerShortcut, handleKeyDown, effectiveConfig.disabled]);

  // Métodos utilitários expostos
  const utils = {
    register: registerShortcut,
    normalize: normalizeCombo,
    match: combosMatch,
    isInput: isInputElement,
    getActiveShortcuts: () => Array.from(activeShortcuts.current.keys()),
    isDisabled: effectiveConfig.disabled
  };

  return utils;
};

// Atalhos predefinidos para ações comuns
export const SHORTCUTS = {
  // Navegação
  GO_TO_DASHBOARD: 'Ctrl+Shift+H',
  GO_TO_CLIENTS: 'Ctrl+Shift+C',
  GO_TO_PROPERTIES: 'Ctrl+Shift+P',
  GO_TO_TASKS: 'Ctrl+Shift+T',
  GO_TO_CALENDAR: 'Ctrl+Shift+L',
  GO_TO_REPORTS: 'Ctrl+Shift+R',
  
  // Ações
  NEW_CLIENT: 'Ctrl+N',
  NEW_PROPERTY: 'Ctrl+Shift+N',
  NEW_TASK: 'Ctrl+Alt+T',
  NEW_NOTE: 'Ctrl+Alt+N',
  
  // Edição
  EDIT: 'Ctrl+E',
  SAVE: 'Ctrl+S',
  DELETE: 'Ctrl+D',
  CANCEL: 'Escape',
  
  // WhatsApp e comunicação
  WHATSAPP: 'Ctrl+W',
  EMAIL: 'Ctrl+Shift+E',
  CALL: 'Ctrl+Alt+C',
  
  // Utilitários
  SEARCH: 'Ctrl+K',
  HELP: 'F1',
  REFRESH: 'Ctrl+R',
  PRINT: 'Ctrl+P',
  EXPORT: 'Ctrl+Shift+S',
  
  // Modais
  CLOSE_MODAL: 'Escape',
  CONFIRM: 'Enter',
  PREVIOUS: 'ArrowLeft',
  NEXT: 'ArrowRight'
};

// Hook específico para ClientDetails
export const useClientDetailsShortcuts = (handlers, deps = []) => {
  const {
    onEdit,
    onDelete,
    onWhatsApp,
    onEmail,
    onCall,
    onNewNote,
    onNewTask,
    onExport,
    onClose
  } = handlers;

  const shortcuts = {
    [SHORTCUTS.EDIT]: (e) => {
      e.preventDefault();
      onEdit?.();
    },
    [SHORTCUTS.DELETE]: (e) => {
      e.preventDefault();
      onDelete?.();
    },
    [SHORTCUTS.WHATSAPP]: (e) => {
      e.preventDefault();
      onWhatsApp?.();
    },
    [SHORTCUTS.EMAIL]: (e) => {
      e.preventDefault();
      onEmail?.();
    },
    [SHORTCUTS.CALL]: (e) => {
      e.preventDefault();
      onCall?.();
    },
    [SHORTCUTS.NEW_NOTE]: (e) => {
      e.preventDefault();
      onNewNote?.();
    },
    [SHORTCUTS.NEW_TASK]: (e) => {
      e.preventDefault();
      onNewTask?.();
    },
    [SHORTCUTS.EXPORT]: (e) => {
      e.preventDefault();
      onExport?.();
    },
    [SHORTCUTS.CLOSE_MODAL]: (e) => {
      e.preventDefault();
      onClose?.();
    }
  };

  // Filtrar apenas handlers definidos
  const activeShortcuts = Object.fromEntries(
    Object.entries(shortcuts).filter(([_, handler]) => handler !== undefined)
  );

  return useKeyboardShortcuts(activeShortcuts, {
    ignoreInputs: true,
    showToast: process.env.NODE_ENV === 'development'
  });
};

// Componente helper para mostrar atalhos disponíveis
export const ShortcutsHelp = ({ shortcuts = SHORTCUTS }) => {
  const isMobile = useIsMobile();
  
  if (isMobile) return null; // Não mostrar em dispositivos móveis

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-slate-200 p-4 max-w-sm z-50">
      <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
        <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">⌨️</kbd>
        Atalhos disponíveis
      </h3>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {Object.entries(shortcuts).map(([action, combo]) => (
          <div key={action} className="flex justify-between items-center text-sm">
            <span className="text-slate-600">{action.replace(/_/g, ' ')}</span>
            <div className="flex gap-1 flex-wrap justify-end">
              {combo.split('+').map((key, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-slate-400">+</span>}
                  <kbd className="px-2 py-1 bg-slate-100 rounded text-xs font-mono whitespace-nowrap">
                    {key}
                  </kbd>
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default useKeyboardShortcuts;